import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useFormatters } from '../../../../hooks';
import { isDriverOnPairedOutbound } from '../../../../helpers/utils/trip';
import { cancelTripDriver, cancelTripDriverEstimate } from '../../../../redux/Trips/actions';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputBase,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
} from '../../../../ui/core';
import { CloseIcon, WarningIcon } from '../../../../ui/icons';

const CANCEL_REASONS = [
  'Can’t make it to the location on time',
  'Late Return Ride',
  'Family or Health Emergency',
  'Vehicle Difficulties',
  'Passenger Issue',
  'Don’t Want to Do the Ride',
];

const CANCEL_PENALTIES = [{ value: 0, label: 'No Penalty' }];

const ActionCancel = ({ trip, outboundTrip, waiting, onWaitingChange, onClose }) => {
  const dispatch = useDispatch();
  const [outboundWarningOpen, setOutboundWarningOpen] = useState(false);
  const [cancelEstimate, setCancelEstimate] = useState(null);
  const [reason, setReason] = useState(CANCEL_REASONS[0]);
  const [otherReason, setOtherReason] = useState('');
  const otherReasonInputRef = useRef(null);
  const [penalty, setPenalty] = useState(CANCEL_PENALTIES[0].value);
  const f = useFormatters();

  const getCancelEstimate = async () => {
    onWaitingChange(true);

    try {
      const apiResponse = await cancelTripDriverEstimate(trip.id)(dispatch);
      const { success, error, estimate } = apiResponse;

      if (success && estimate) {
        setCancelEstimate(estimate);
        setPenalty(estimate.penalty);
        return;
      }

      throw new Error(error);
    } catch (err) {
      console.error('❌ getCancelEstimate error:', err);
    } finally {
      onWaitingChange(false);
    }
  };

  const cancelDriver = async () => {
    onWaitingChange(true);
    await cancelTripDriver(trip.id, { reason: `Admin: ` + (reason !== 'Other' ? reason : otherReason), penalty })(
      dispatch,
    );
    onWaitingChange(false);
    setCancelEstimate(null);
    onClose();
  };

  const startCancel = () => {
    if (isDriverOnPairedOutbound(trip, outboundTrip)) {
      setOutboundWarningOpen(true);
      return;
    }
    getCancelEstimate();
  };

  const confirmOutboundWarning = () => {
    setOutboundWarningOpen(false);
    getCancelEstimate();
  };

  const dismissOutboundWarning = () => {
    setOutboundWarningOpen(false);
    onClose();
  };

  const giveUp = () => {
    onWaitingChange(false);
    setCancelEstimate(null);
    onClose();
  };

  const reasons = [...CANCEL_REASONS];
  const pointDescription = cancelEstimate?.penalty === 1 ? 'point' : 'points';

  const penalties = [
    ...(cancelEstimate
      ? [{ value: cancelEstimate.penalty, label: `Deducted ${cancelEstimate?.penalty} ${pointDescription}` }]
      : []),
    ...CANCEL_PENALTIES,
  ];

  useEffect(() => {
    if (reason === 'Other' && otherReasonInputRef.current) {
      otherReasonInputRef.current.focus();
    }
  }, [reason]);

  return (
    <>
      <MenuItem disabled={waiting} onClick={startCancel}>
        {!waiting && <CloseIcon color="error" />}
        {waiting && <CircularProgress size={16} />}
        <Typography color="error" ml="5px">
          Cancel driver
        </Typography>
      </MenuItem>
      <Dialog open={outboundWarningOpen} onClose={dismissOutboundWarning} aria-labelledby="outbound-warning-title">
        <DialogTitle id="outbound-warning-title" sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <WarningIcon color="warning" />
          Driver is also on the outbound trip
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {[trip.driver?.firstName, trip.driver?.lastName].filter(Boolean).join(' ') || 'This driver'} is also the
            driver on the outbound leg of this trip. Cancelling them from the return may leave these riders without a
            ride home.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={dismissOutboundWarning}>Go back</Button>
          <Button variant="contained" color="error" onClick={confirmOutboundWarning}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!cancelEstimate} onClose={giveUp}>
        <DialogTitle sx={{ padding: '12px 16px', color: 'text.secondary', fontSize: 16 }}>
          Fill out the cancellation request
        </DialogTitle>
        <DialogContent sx={{ padding: 0 }}>
          <Box sx={{ backgroundColor: 'divider', padding: '2px 16px' }}>
            <Typography color="text.secondary" variant="body2">
              Reason
            </Typography>
          </Box>
          <Box sx={{ padding: '10px 16px' }}>
            {
              <FormControl>
                <RadioGroup value={reason} onChange={(e) => setReason(e.target.value)}>
                  {reasons.map((reason) => (
                    <FormControlLabel key={reason} value={reason} control={<Radio />} label={reason} />
                  ))}
                  <FormControlLabel
                    control={<Radio />}
                    label={
                      <InputBase
                        inputRef={otherReasonInputRef}
                        placeholder="Other"
                        disabled={reason !== 'Other'}
                        value={otherReason}
                        onChange={(e) => setOtherReason(e.target.value)}
                      />
                    }
                    value="Other"
                  />
                </RadioGroup>
              </FormControl>
            }
          </Box>

          <Box sx={{ backgroundColor: 'divider', padding: '2px 16px' }}>
            <Typography color="text.secondary" variant="body2">
              Penalty
            </Typography>
          </Box>
          <Box sx={{ padding: '10px 16px' }}>
            {
              <FormControl>
                <RadioGroup value={penalty} onChange={(e) => setPenalty(Number.parseFloat(e.target.value))}>
                  {penalties.map((p) => (
                    <FormControlLabel key={p.label} value={p.value} control={<Radio />} label={p.label} />
                  ))}
                </RadioGroup>
              </FormControl>
            }
          </Box>

          {cancelEstimate?.payoutDetails ? (
            <>
              <Box sx={{ backgroundColor: 'divider', padding: '2px 16px' }}>
                <Typography color="text.secondary" variant="body2">
                  Payout Details
                </Typography>
              </Box>
              <Box sx={{ padding: '10px 16px', opacity: 0.8 }}>
                {
                  <Typography variant="body2">
                    {cancelEstimate.payoutDetails.origOutboundPayoutTitle}:{' '}
                    {f.number.currency(cancelEstimate.payoutDetails.origOutboundPayout / 100)}
                  </Typography>
                }
                {
                  <Typography variant="body2">
                    {cancelEstimate.payoutDetails.newOutboundPayoutTitle}:{' '}
                    {f.number.currency(cancelEstimate.payoutDetails.newOutboundPayout / 100)}
                  </Typography>
                }
              </Box>
            </>
          ) : null}
        </DialogContent>
        <Button
          sx={{
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
            width: '100%',
          }}
          size="large"
          variant="contained"
          color="error"
          onClick={cancelDriver}
        >
          Cancel driver
        </Button>
      </Dialog>
    </>
  );
};

export default ActionCancel;
